import { useEffect, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';

import axiosClient from '../../../../../utils/axios';
import { setPostDataAttribute } from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
import { lomavisShowToastr } from '@/utils/ui/toastr';
import { Tag } from './Tagger';

export type Catalog = {
  catalog_id: string;
  catalog_name: string;
  shop_name: string;
  product_count: number;
};
interface Props {
  selectedCatalog: Catalog;
  setSelectedCatalog: React.Dispatch<React.SetStateAction<Catalog>>;
  setEmptyCatalog: React.Dispatch<React.SetStateAction<boolean>>;
  tags: Tag[];
  instagramUserTags: any[];
}
export const ProductCatalogSelection: React.FC<Props> = ({
  selectedCatalog,
  setSelectedCatalog,
  setEmptyCatalog,
  tags,
  instagramUserTags
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const { instagram_shopping } = useSelector(
    (state: any) => state.lomavisCreator.lomavisCreatorConfig
  );

  const [catalogList, setCatalogList] = useState<Catalog[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  // const hasProductInfo = tags.some((tag) => !!tag.productInfo);

  const hasProductInfo = Object.values(instagramUserTags || {}).some((tags) =>
    tags.some((tag) => !!tag.productInfo)
  );

  useEffect(() => {
    const url = instagram_shopping?.catalog_url;
    if (!url) {
      lomavisShowToastr(
        t('lomavisCreator.additionalOptions.catalogSearchNotAvailable'),
        'error'
      );
      setIsLoading(false);
      return;
    }

    axiosClient
      .get(url)
      .then((res) => {
        const data = res.data?.data || [];

        if (data.length === 0) {
          setEmptyCatalog(true);
          lomavisShowToastr(
            t('lomavisCreator.additionalOptions.noCatalogsFound'),
            'error'
          );
        } else {
          !selectedCatalog && setSelectedCatalog(data[0]);
        }

        setCatalogList(data);
      })
      .catch((error) => {
        console.error('Error fetching catalogs:', error);

        lomavisShowToastr(
          t('lomavisCreator.additionalOptions.catalogFetchError'),
          'error'
        );
        setCatalogList([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const catalogId = e.target.value;
    const selectedCatalog = catalogList.find((c) => c.catalog_id === catalogId);

    if (selectedCatalog) {
      setSelectedCatalog(selectedCatalog); // ✅ set full object in local state
    }
  };

  return (
    <div className="w-100">
      <Form.Group className="mb-3">
        {/* Show spinner while loading */}
        {isLoading && !catalogList.length && !selectedCatalog ? (
          <div className="w-100 d-flex justify-content-center">
            <Spinner animation="border" size="sm" />
          </div>
        ) : null}

        {/* Show select dropdown when there's a selectedCatalog or when catalogList is available */}
        {(selectedCatalog || catalogList.length > 0) && (
          <Form.Select
            className="form-select border-primary shadow-sm w-100"
            value={selectedCatalog?.catalog_id ?? ''}
            onClick={(e) => {
              if (hasProductInfo) {
                e.preventDefault();
                lomavisShowToastr(
                  t('lomavisCreator.additionalOptions.catalogChangeDisabled'),
                  'error'
                );
              }
            }}
            onChange={(e) => {
              if (hasProductInfo) {
                e.preventDefault();
                return;
              }
              handleChange(e);
            }}
            disabled={!catalogList.length}
          >
            {/* Default fallback when no options exist */}
            {!catalogList.length && !selectedCatalog ? (
              <option disabled value="">
                {t('lomavisCreator.additionalOptions.noCatalogs')}
              </option>
            ) : null}

            {/* Render selectedCatalog first if present and not in catalogList */}
            {selectedCatalog &&
              !catalogList.some(
                (c) => c.catalog_id === selectedCatalog.catalog_id
              ) && (
                <option value={selectedCatalog.catalog_id}>
                  {selectedCatalog.catalog_name} (
                  {selectedCatalog.product_count}{' '}
                  {selectedCatalog.product_count === 1
                    ? t('lomavisCreator.additionalOptions.IGProduct')
                    : t('lomavisCreator.additionalOptions.IGProducts')}
                  )
                </option>
              )}

            {/* Render full list */}
            {catalogList.map((catalog) => (
              <option key={catalog.catalog_id} value={catalog.catalog_id}>
                {catalog.catalog_name} ({catalog.product_count}{' '}
                {catalog.product_count === 1
                  ? t('lomavisCreator.additionalOptions.IGProduct')
                  : t('lomavisCreator.additionalOptions.IGProducts')}
                )
              </option>
            ))}
          </Form.Select>
        )}
      </Form.Group>

      {/* Show "No catalogs" message when nothing is selected and list is empty */}
      {!selectedCatalog && !catalogList.length && (
        <span className="text-muted">
          {t('lomavisCreator.additionalOptions.noCatalogs')}
        </span>
      )}
    </div>
  );
};
